import React, { useCallback, useContext } from 'react'

import ModalBody from 'LuxKit/Modal/ModalBody'
import ModalContent from 'LuxKit/Modal/ModalContent'
import ModalFooter from 'LuxKit/Modal/ModalFooter'
import ModalHeader from 'LuxKit/Modal/ModalHeader'
import BodyText from 'LuxKit/Typography/BodyText'
import { ModalElementContext } from 'contexts/ModalProvider'
import noop from 'lib/function/noop'
import { DeleteTripError } from 'tripPlanner/api/types'
import { useDeleteTrip } from 'tripPlanner/hooks/api/trip'
import useModalCallbacks from 'tripPlanner/hooks/View/useModalCallbacks'
import { BasicTrip, FullTrip } from 'tripPlanner/types/common'
import config from 'constants/config'
import { capitaliseAll } from 'lib/string/stringUtils'

interface Props {
  trip: BasicTrip | FullTrip
  onDelete?: () => void
}

function DeleteTrip({ trip, onDelete = noop }: Props) {
  const { id: tripId, name } = trip

  const { closeModal, goBack } = useModalCallbacks()

  const modalElement = useContext(ModalElementContext)

  const onCloseModalClicked = useCallback(() => {
    closeModal()
    modalElement?.resolve()
  }, [closeModal, modalElement])

  const { mutate, isLoading } = useDeleteTrip({
    onSuccess: () => {
      onCloseModalClicked()
      onDelete()
    },
    onError: (e: DeleteTripError) => {
      if (e.status === 404) {
        throw new Error('Delete failed. Trip not found.')
      }
    },
  })

  const deleteTrip = useCallback(() => {
    mutate(tripId)
  }, [mutate, tripId])

  return (
    <>
      <ModalHeader
        title="Delete trip"
        onBackButtonClick={goBack}
        dismissible={false}
      />
      <ModalBody>
        <ModalContent>
          <BodyText variant="large" colour="neutral-one" as="p">
            Are you sure you want to delete{' '}
            <BodyText as="span" variant="large" weight="bold">
              {name}
            </BodyText>
            ?
          </BodyText>
          <BodyText variant="large" colour="neutral-one" as="p">
            This action cannot be undone and will permanently delete your trip.
            Existing bookings will not be affected and can still be accessed in
            My {capitaliseAll(config.brandConfig.purchaseSynonym)}s.
          </BodyText>
        </ModalContent>
      </ModalBody>
      <ModalFooter
        primaryActionProps={{
          children: 'Yes, delete trip',
          disabled: isLoading,
          onClick: deleteTrip,
        }}
        secondaryActionProps={{
          children: 'Cancel',
          disabled: isLoading,
          onClick: onCloseModalClicked,
        }}
      />
    </>
  )
}

export default DeleteTrip
